import * as Sentry from '@sentry/vue'
import type { App } from 'vue'
import native from 'src/config/native.js'
import {isIgnoredChunkLoadError} from '@/composables/utils/useChunkPreloadErrorHandling.ts'

export const initSentry = (app: App<Element>) => {
  if (import.meta.env.VITE_SENTRY_ACTIVATED !== 'true') return

  Sentry.init({
    app: app,
    dsn: import.meta.env.VITE_SENTRY_URL,
    environment: import.meta.env.VITE_ENV,
    // We need to disable browser session tracking because it would put a high load on our Sentry server.
    // To do so, we must remove the BrowserSession integration.
    integrations: function (integrations) {
      return integrations.filter(function (integration) {
        return integration.name !== 'BrowserSession'
      })
    },
    beforeSend(event, hint) {
      if (!hint) return null

      const error = hint.originalException as Error
      const errorMessage = error?.message || String(error)
      if (!errorMessage) return null

      // Handled by useChunkPreloadErrorHandling.ts
      if (isIgnoredChunkLoadError(errorMessage)) return null

      if (errorMessage.includes('Request failed with status code 40')) return null
      if (errorMessage.includes('Request failed with status code 503')) return null

      // warning: maybe it is not only connected to users internet
      if (errorMessage === 'Network Error') return null
      if (errorMessage === 'NavigationDuplicated') return null
      if (errorMessage.includes("Can't find variable: Initialize")) return null
      if (
        errorMessage.includes('InvalidStateError: Failed to execute ') &&
        errorMessage.includes('there is no application cache to update')
      )
        return null
      if (errorMessage.includes('Navigating to current location') && errorMessage.includes('is not allowed')) return null
      if (errorMessage.includes('Redirected when going from') && errorMessage.includes('via a navigation guard')) return null
      // error: everytime a user logs out a request aborted error is thrown
      if (errorMessage.includes('Request aborted')) return null

      return event
    },
  })

  const scope = Sentry.getCurrentScope()
  native.getVersion().then(appVersion => {
    scope.setTag('version', import.meta.env.BASE_VERSION)
    scope.setTag('app-type', native.getDevice())
    scope.setTag('app-version', appVersion)
    scope.setTag('environment', import.meta.env.VITE_VERSION_TYPE)
  })
}
